/**
 * A queue line's recorded decision, as the proposal and price-step tables show it (bata-ui#166
 * W0-9, bata-platform ADR 6045, DEC-32).
 *
 * The platform publishes each line's FIRST recorded decision from the append-only trail —
 * `disposition`, `disposition_reason_code`, `disposition_decided_at`, and the adjusted value when
 * the decision was `adjust` — with `null` meaning pending. One cell renders all of it, so a
 * decided line reads as decided wherever it is listed, and a pending one says pending rather than
 * showing nothing. The words are the transfer queue's (`dispositionStateKey`): one vocabulary for
 * the three queues, as the platform chose one set of field names for them. `approve` reads
 * "Approved · not sent", because an approval raises a command and sends nothing.
 *
 * **`superseded`** (W1-6, ADR 6048): a newer run of the market took the undecided line over. The
 * cell names that run and when — the line is history, never offered, and its decision belongs on
 * the newer run (`./superseded.tsx`).
 *
 * **One line (client rule 2, bata-ui#196 Package B, `docs/adr/9028`).** A table row is one line.
 * The cell draws the decision's word and, for an `adjust`, the value it was adjusted to — the one
 * secondary item that changes what the line will do. Who decided, when, and the reason code are
 * the line's detail: they are in the cell's `title` (the tooltip) and in its accessible name, so
 * nothing is lost, but they no longer wrap the row to two, three or four lines.
 *
 * **Drawn to the designer's brief (part 1 §1, pkgC2).** The decision's word is a `StatusMark`: a
 * mono symbol and the word on one line at 600, never the colour alone — ● pending (and
 * deferred), ✓ approved, ✕ rejected, ◐ adjusted, — superseded or unrecognised. The words stay
 * the queue's own (`dispositionStateKey`): «Approved · not sent» says in the same breath that
 * nothing was sent, which the brief's example word «Approvata» would drop.
 *
 * **The «Reason» column** (`DecisionReason`): a rejection's reason in ink, any other reason in
 * the label grey; the decision, who took it and its day in the tooltip, which opens on focus. Who
 * is the platform's (`disposition_actor_name`, bata-platform ADR 9021, bata-ui issue 218 Phase 3),
 * by name, «System» or the id (`personText`); a decision whose read names nobody says no one rather
 * than an invented name. The table's «Decided by» column is `decidedByColumn` (`@/planning`). A line
 * with no reason leaves the cell EMPTY, not «—»: an undecided line has no reason to be missing,
 * so there is no absence to state (and nothing registers with the table's `AbsenceNotes`).
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { formatDate } from '@/lib/format';
import { StatusMark, TruncatedText, type StatusMarkKind } from '@/primitives';

import { SUPERSEDED } from './superseded';
import { dispositionStateKey } from './transfer-dispositions';

export interface DecisionCellProps {
  /** `null` (or absent on a platform before v0.50) is pending. */
  readonly disposition: string | null;
  readonly reasonCode: string | null;
  readonly decidedAt: string | null;
  /** The adjusted units or price, already formatted by the caller where it is money. */
  readonly adjusted: number | string | null;
  /** For a superseded line: the newer run that took it over, and when (platform v0.51). */
  readonly supersededByRunId?: string | null;
  readonly supersededAt?: string | null;
  /**
   * The governed reason code's label from the vocabulary the page already read
   * (`reasonLabelOf`); the code itself where the vocabulary has none.
   */
  readonly reasonLabel?: ((code: string) => string) | undefined;
  /**
   * Who decided, already in words (`decidedByText`): the profile name, «System», or the id.
   * `null` or omitted where the read names nobody — the tooltip then names nobody.
   */
  readonly decidedBy?: string | null | undefined;
}

/** A governed reason vocabulary as the platform publishes it: a code and its own prose. */
export interface GovernedReason {
  readonly code: string;
  readonly label: { readonly text: string };
}

/**
 * The label a governed reason code carries in the vocabulary already read — never the raw code
 * when a label exists; the code, verbatim, when the vocabulary is not read or does not know it.
 */
export function reasonLabelOf(vocabulary: readonly GovernedReason[] | undefined): (code: string) => string {
  const byCode = new Map((vocabulary ?? []).map((option) => [option.code, option.label.text]));
  return (code) => byCode.get(code) ?? code;
}

/**
 * The reason of a DECIDED line, as the «Reason» column shows it: the governed code's label. `null`
 * for a pending or superseded line (nothing was decided on it) and for a decision recorded
 * without a code. The same reason `decisionDetails` puts first in the cell's tooltip.
 */
export function decisionReason({ disposition, reasonCode, reasonLabel }: DecisionCellProps): string | null {
  if (disposition === null || disposition === SUPERSEDED || reasonCode === null) return null;
  return reasonLabel === undefined ? reasonCode : reasonLabel(reasonCode);
}

/**
 * The mark each recorded decision is drawn with (brief part 1 §1). A deferral is still to be
 * decided, so it takes the pending mark with its own word; a superseded line is history and an
 * unrecognised decision is not one this screen can vouch for, so both take the neutral dash.
 */
export function decisionMark(disposition: string | null): StatusMarkKind {
  switch (disposition) {
    case null:
    case 'defer': return 'open';
    case 'approve': return 'good';
    case 'reject': return 'critical';
    case 'adjust': return 'modified';
    default: return 'neutral';
  }
}

/**
 * «by a more recent run on date» for a superseded line as words; `null` when the platform named
 * neither. The run is not named by its id (audit A1): it is the newer run of the same market, and
 * the id stays on the element (`data-superseded-by`) for support.
 */
function supersededText(
  t: TFunction, locale: string, byRunId: string | null, at: string | null,
): string | null {
  if (byRunId === null && at === null) return null;
  if (byRunId === null) return t('inseason.superseded.on', { when: formatDate(at ?? '', locale, 'UTC') });
  if (at === null) return t('inseason.superseded.byRun');
  return t('inseason.superseded.byRunOn', { when: formatDate(at, locale, 'UTC') });
}

/** "by run X on date" for a superseded line; nothing when the platform named neither. */
export function SupersededNote({ byRunId, at }: { readonly byRunId: string | null; readonly at: string | null }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const text = supersededText(t, i18n.language, byRunId, at);
  if (text === null) return null;
  return (
    <span className="text-caption text-ink-secondary" data-superseded-by={byRunId ?? ''}>
      {text}
    </span>
  );
}

/**
 * The line's detail as words, in reading order: for a superseded line the run that took it over
 * and when; for a decided one the reason (`decisionReason`: the governed code's label where the
 * vocabulary was read, else the platform's code verbatim — data, not copy) and the day. Empty for
 * a pending line, which has none.
 */
export function decisionDetails(
  t: TFunction,
  locale: string,
  props: DecisionCellProps,
): readonly string[] {
  const { disposition, decidedAt, supersededByRunId = null, supersededAt = null } = props;
  if (disposition === SUPERSEDED) {
    const text = supersededText(t, locale, supersededByRunId, supersededAt);
    return text === null ? [] : [text];
  }
  if (disposition === null) return [];
  const sentence = decisionSentence(t, locale, { ...props, decidedAt });
  return sentence === null ? [] : [sentence];
}

/**
 * A decided line as ONE catalogue sentence (issue 249): the decision, who, the day and the reason —
 * «Approvata da M. Ríos il 29 set 2026 · Copertura già sopra il massimo». Each part only where the
 * read publishes it; never pieces joined by a list pattern, which read «…, da M. Ríos e il 29 set».
 * `null` when there is nothing beyond the word on the line. Pure.
 */
export function decisionSentence(t: TFunction, locale: string, props: DecisionCellProps): string | null {
  const reason = decisionReason(props);
  const decision = t(dispositionStateKey(props.disposition));
  const who = props.decidedBy ?? null;
  const named = who !== null && who !== '';
  const when = props.decidedAt === null ? null : formatDate(props.decidedAt, locale, 'UTC');
  if (reason !== null) {
    if (when === null) {
      return named
        ? t('inseason.decision.reasonTooltipUndatedBy', { decision, who, reason })
        : t('inseason.decision.reasonTooltipUndated', { decision, reason });
    }
    return named
      ? t('inseason.decision.reasonTooltipBy', { decision, who, when, reason })
      : t('inseason.decision.reasonTooltip', { decision, when, reason });
  }
  if (when === null) return named ? t('inseason.decision.tooltipUndatedBy', { decision, who }) : null;
  return named
    ? t('inseason.decision.tooltipBy', { decision, who, when })
    : t('inseason.decision.tooltip', { decision, when });
}

export function DecisionCell(props: DecisionCellProps): React.JSX.Element {
  const { disposition, adjusted } = props;
  const { t, i18n } = useTranslation();
  // One sentence from the catalogue (issue 249) — never parts joined by a list pattern.
  const detail = decisionDetails(t, i18n.language, props)[0] ?? null;
  const secondary = disposition !== null && disposition !== SUPERSEDED && adjusted !== null
    ? t('inseason.decision.adjustedTo', { value: adjusted })
    : null;
  return (
    <span
      className="inline-flex items-center gap-x-2 whitespace-nowrap"
      data-decision={disposition ?? 'pending'}
      {...(detail === null ? {} : { title: detail })}
    >
      <StatusMark mark={decisionMark(disposition)} labelKey={dispositionStateKey(disposition)} />
      {secondary !== null && (
        <span className="text-caption text-ink-secondary font-numeric">{secondary}</span>
      )}
      {detail !== null && <span className="sr-only">{detail}</span>}
    </span>
  );
}

/**
 * The «Reason» column's content (brief part 1 §1, pkgC2) — put it inside the column's own
 * `<td>`. The reason on one line (`TruncatedText`), in ink for a rejection and in the label grey
 * otherwise; the tooltip, open on hover and on focus, says the decision, its day and the whole
 * reason: «Rejected by M. Ríos on 29 Sept 2026 · Coverage already above the maximum» — who only
 * where the read names someone. Nothing at all for a line with no reason (see the module note).
 */
export function DecisionReason(props: DecisionCellProps): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const reason = decisionReason(props);
  if (reason === null) return null;
  const detail = decisionSentence(t, i18n.language, props) ?? reason;
  return (
    <TruncatedText
      text={reason}
      detail={detail}
      className={props.disposition === 'reject' ? 'text-ink-primary' : 'text-ink-label'}
    />
  );
}
